import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from "react";
import ReactFlow, {
  addEdge,
  useNodesState,
  useEdgesState,
  Controls,
  Background,
  MiniMap,
  Handle,
  Position,
} from "reactflow";
import "reactflow/dist/style.css";
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  LinearProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import EditNoteIcon from "@mui/icons-material/EditNote";
import DownloadIcon from "@mui/icons-material/Download";
import { useNavigate } from "react-router-dom";
import api from "../api";

// ══════════════════════════════════════════════════════════════
// CONSTANTS
// ══════════════════════════════════════════════════════════════

const PRIMARY = "#005E60";
const DARK = "#1a1a2e";

const COMPONENT_TYPES = [
  { type: "GEN", label: "Generator", color: "#2e7d32" },
  { type: "SWBDS", label: "Switchboard", color: "#1565c0" },
  { type: "TRANS", label: "Transformer", color: "#6a1b9a" },
  { type: "VFD", label: "VFD", color: "#e65100" },
  { type: "MOTOR", label: "Motor", color: "#ad1457" },
  { type: "UPS", label: "UPS", color: "#00838f" },
  { type: "DBR", label: "Dyn. Brick Resistor", color: "#4e342e" },
];

const VOLTAGE_SYSTEMS = [
  "440V / 60Hz",
  "480V / 60Hz",
  "690V / 60Hz",
  "3.3kV / 60Hz",
  "6.6kV / 60Hz",
  "11kV / 60Hz",
  "440V / 50Hz",
  "690V / 50Hz",
  "6.6kV / 50Hz",
  "11kV / 50Hz",
];

const CLASSIFICATION_SOCIETIES = [
  "DNV",
  "LR",
  "ABS",
  "BV",
  "RINA",
  "CCS",
  "NK",
  "NAVSEA",
];

const VESSEL_TYPES = [
  "Offshore Supply Vessel",
  "Cargo Ship",
  "Tanker",
  "Ferry",
  "Cruise Ship",
  "Naval Vessel",
  "Research Vessel",
  "Dredger",
  "Tug",
  "Other",
];

// Fields shown as editable in the dialog
const BASIC_SPEC_KEYS = [
  "ac_voltage",
  "frequency_hz",
  "power_rating_kw",
  "space_taken_mm",
  "ambient_temp_c",
  "quantity",
  "notes",
];

const INITIAL_PROJECT = {
  project_number: "",
  customer_name: "",
  vessel_name: "",
  vessel_type: "",
  classification_society: "",
  flag_state: "",
  voltage_system: "",
  frequency: "",
  space_available_mm: "",
  notes: "",
};

// ══════════════════════════════════════════════════════════════
// IEEE SVG SYMBOLS
// ══════════════════════════════════════════════════════════════

const IEEESymbol = ({ type, color, size = 60 }) => {
  const p = { width: size, height: size };
  switch (type) {
    case "GEN":
      return (
        <svg {...p} viewBox="0 0 60 60">
          <circle
            cx="30"
            cy="30"
            r="26"
            fill="none"
            stroke={color}
            strokeWidth="2.5"
          />
          <text
            x="30"
            y="26"
            textAnchor="middle"
            fontSize="11"
            fontWeight="bold"
            fill={color}
            fontFamily="Arial"
          >
            G
          </text>
          <path
            d="M18 34 Q21 28 24 34 Q27 40 30 34 Q33 28 36 34 Q39 40 42 34"
            fill="none"
            stroke={color}
            strokeWidth="1.8"
          />
        </svg>
      );
    case "MOTOR":
      return (
        <svg {...p} viewBox="0 0 60 60">
          <circle
            cx="30"
            cy="30"
            r="26"
            fill="none"
            stroke={color}
            strokeWidth="2.5"
          />
          <text
            x="30"
            y="35"
            textAnchor="middle"
            fontSize="14"
            fontWeight="bold"
            fill={color}
            fontFamily="Arial"
          >
            M
          </text>
        </svg>
      );
    case "TRANS":
      return (
        <svg {...p} viewBox="0 0 60 60">
          <circle
            cx="20"
            cy="30"
            r="13"
            fill="none"
            stroke={color}
            strokeWidth="2.5"
          />
          <circle
            cx="40"
            cy="30"
            r="13"
            fill="none"
            stroke={color}
            strokeWidth="2.5"
          />
          <line x1="2" y1="30" x2="7" y2="30" stroke={color} strokeWidth="2" />
          <line
            x1="53"
            y1="30"
            x2="58"
            y2="30"
            stroke={color}
            strokeWidth="2"
          />
        </svg>
      );
    case "SWBDS":
      return (
        <svg {...p} viewBox="0 0 60 60">
          <rect
            x="6"
            y="12"
            width="48"
            height="36"
            rx="3"
            fill="none"
            stroke={color}
            strokeWidth="2.5"
          />
          <line
            x1="6"
            y1="22"
            x2="54"
            y2="22"
            stroke={color}
            strokeWidth="1.5"
          />
          <line
            x1="18"
            y1="22"
            x2="18"
            y2="48"
            stroke={color}
            strokeWidth="1.5"
          />
          <line
            x1="30"
            y1="22"
            x2="30"
            y2="48"
            stroke={color}
            strokeWidth="1.5"
          />
          <line
            x1="42"
            y1="22"
            x2="42"
            y2="48"
            stroke={color}
            strokeWidth="1.5"
          />
          <rect
            x="14"
            y="26"
            width="8"
            height="5"
            rx="1"
            fill={color}
            opacity="0.7"
          />
          <rect
            x="26"
            y="26"
            width="8"
            height="5"
            rx="1"
            fill={color}
            opacity="0.7"
          />
          <rect
            x="38"
            y="26"
            width="8"
            height="5"
            rx="1"
            fill={color}
            opacity="0.7"
          />
        </svg>
      );
    case "VFD":
      return (
        <svg {...p} viewBox="0 0 60 60">
          <rect
            x="6"
            y="10"
            width="48"
            height="40"
            rx="4"
            fill="none"
            stroke={color}
            strokeWidth="2.5"
          />
          <text
            x="30"
            y="27"
            textAnchor="middle"
            fontSize="9"
            fill={color}
            fontFamily="Arial"
            fontWeight="bold"
          >
            VFD
          </text>
          <path
            d="M12 38 Q16 32 20 38 Q24 44 28 38"
            fill="none"
            stroke={color}
            strokeWidth="1.8"
          />
          <path
            d="M32 38 L46 38 M42 34 L46 38 L42 42"
            fill="none"
            stroke={color}
            strokeWidth="1.8"
          />
        </svg>
      );
    case "UPS":
      return (
        <svg {...p} viewBox="0 0 60 60">
          <rect
            x="6"
            y="10"
            width="48"
            height="40"
            rx="4"
            fill="none"
            stroke={color}
            strokeWidth="2.5"
          />
          <text
            x="30"
            y="24"
            textAnchor="middle"
            fontSize="9"
            fill={color}
            fontFamily="Arial"
            fontWeight="bold"
          >
            UPS
          </text>
          <rect
            x="16"
            y="28"
            width="22"
            height="12"
            rx="2"
            fill="none"
            stroke={color}
            strokeWidth="1.8"
          />
          <rect x="38" y="31" width="4" height="6" rx="1" fill={color} />
          <line
            x1="21"
            y1="34"
            x2="25"
            y2="34"
            stroke={color}
            strokeWidth="1.5"
          />
          <line
            x1="23"
            y1="32"
            x2="23"
            y2="36"
            stroke={color}
            strokeWidth="1.5"
          />
          <line
            x1="29"
            y1="34"
            x2="35"
            y2="34"
            stroke={color}
            strokeWidth="1.5"
          />
        </svg>
      );
    case "DBR":
      return (
        <svg {...p} viewBox="0 0 60 60">
          <line x1="30" y1="2" x2="30" y2="12" stroke={color} strokeWidth="2" />
          <polyline
            points="30,12 22,16 38,20 22,24 38,28 22,32 38,36 30,40"
            fill="none"
            stroke={color}
            strokeWidth="2"
            strokeLinejoin="round"
          />
          <line
            x1="30"
            y1="40"
            x2="30"
            y2="50"
            stroke={color}
            strokeWidth="2"
          />
          <line
            x1="20"
            y1="50"
            x2="40"
            y2="50"
            stroke={color}
            strokeWidth="2"
          />
          <line
            x1="23"
            y1="53"
            x2="37"
            y2="53"
            stroke={color}
            strokeWidth="1.5"
          />
          <line
            x1="26"
            y1="56"
            x2="34"
            y2="56"
            stroke={color}
            strokeWidth="1"
          />
          <text
            x="46"
            y="28"
            textAnchor="middle"
            fontSize="7"
            fontWeight="bold"
            fill={color}
            fontFamily="Arial"
          >
            DBR
          </text>
        </svg>
      );
    default:
      return (
        <svg {...p} viewBox="0 0 60 60">
          <rect
            x="8"
            y="8"
            width="44"
            height="44"
            rx="4"
            fill="none"
            stroke={color}
            strokeWidth="2.5"
          />
          <text
            x="30"
            y="35"
            textAnchor="middle"
            fontSize="11"
            fill={color}
            fontFamily="Arial"
            fontWeight="bold"
          >
            {type}
          </text>
        </svg>
      );
  }
};

// ══════════════════════════════════════════════════════════════
// COMPONENT NODE
// ══════════════════════════════════════════════════════════════

function ComponentNode({ data }) {
  const comp = COMPONENT_TYPES.find((c) => c.type === data.componentType);
  const [hov, setHov] = useState(false);
  if (!comp) return null;

  const handleStyle = {
    background: comp.color,
    width: 8,
    height: 8,
    border: "2px solid #fff",
  };

  return (
    <Box
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      onClick={(e) => {
        e.stopPropagation();
        data.onOpen?.(data.id);
      }}
      sx={{
        background: "#fff",
        borderRadius: "12px",
        cursor: "pointer",
        border: `2px solid ${hov ? comp.color : "#d0d7de"}`,
        padding: "8px 10px 6px",
        minWidth: 100,
        maxWidth: 120,
        textAlign: "center",
        position: "relative",
        boxShadow: hov
          ? `0 6px 20px ${comp.color}33`
          : "0 2px 8px rgba(0,0,0,0.07)",
        transition: "all 0.15s ease",
        userSelect: "none",
      }}
    >
      {/* Delete button — visible on hover */}
      {hov && (
        <Box
          onClick={(e) => {
            e.stopPropagation();
            data.onDelete?.(data.id);
          }}
          sx={{
            position: "absolute",
            top: -8,
            right: -8,
            width: 20,
            height: 20,
            borderRadius: "50%",
            backgroundColor: "#d32f2f",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "0.62rem",
            fontWeight: "bold",
            cursor: "pointer",
            zIndex: 10,
            boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
          }}
        >
          ✕
        </Box>
      )}

      <Handle
        id="left"
        type="target"
        position={Position.Left}
        style={handleStyle}
      />
      <Handle
        id="right"
        type="source"
        position={Position.Right}
        style={handleStyle}
      />
      <Handle
        id="top"
        type="target"
        position={Position.Top}
        style={handleStyle}
      />
      <Handle
        id="bottom"
        type="source"
        position={Position.Bottom}
        style={handleStyle}
      />

      <Box sx={{ display: "flex", justifyContent: "center", mb: 0.5 }}>
        <IEEESymbol type={data.componentType} color={comp.color} size={50} />
      </Box>
      <Typography
        sx={{
          fontSize: "0.68rem",
          fontWeight: 700,
          color: "#1a1a2e",
          lineHeight: 1.2,
        }}
      >
        {data.label || comp.label}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.58rem",
          color: comp.color,
          fontWeight: 600,
          mt: 0.2,
        }}
      >
        {comp.type}
      </Typography>
      <Box
        sx={{
          mt: 0.4,
          display: "flex",
          flexWrap: "wrap",
          gap: 0.3,
          justifyContent: "center",
        }}
      >
        {data.specs?.ac_voltage && (
          <Chip
            label={`${data.specs.ac_voltage}V`}
            size="small"
            sx={{
              height: 15,
              fontSize: "0.56rem",
              backgroundColor: `${comp.color}18`,
              color: comp.color,
              fontWeight: 600,
            }}
          />
        )}
        {data.specs?.power_rating_kw && (
          <Chip
            label={`${data.specs.power_rating_kw}kW`}
            size="small"
            sx={{
              height: 15,
              fontSize: "0.56rem",
              backgroundColor: `${comp.color}18`,
              color: comp.color,
              fontWeight: 600,
            }}
          />
        )}
      </Box>
    </Box>
  );
}

// ══════════════════════════════════════════════════════════════
// BUS BAR NODE
// ══════════════════════════════════════════════════════════════

function BusBarNode({ data }) {
  const [hov, setHov] = useState(false);

  return (
    <Box
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      sx={{ position: "relative", width: 320, height: 28, cursor: "default" }}
    >
      {/* Delete button on hover */}
      {hov && (
        <Box
          onClick={(e) => {
            e.stopPropagation();
            data.onDelete?.(data.id);
          }}
          sx={{
            position: "absolute",
            top: -10,
            right: -8,
            width: 18,
            height: 18,
            borderRadius: "50%",
            backgroundColor: "#d32f2f",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "0.58rem",
            fontWeight: "bold",
            cursor: "pointer",
            zIndex: 10,
          }}
        >
          ✕
        </Box>
      )}

      {/* Bus label — above the bar */}
      <Typography
        sx={{
          position: "absolute",
          top: -16,
          left: "50%",
          transform: "translateX(-50%)",
          fontSize: "0.6rem",
          color: hov ? "#1a1a2e" : "#546e7a",
          fontWeight: 700,
          whiteSpace: "nowrap",
          pointerEvents: "none",
          letterSpacing: "0.05em",
          textTransform: "uppercase",
        }}
      >
        {data.label || "BUS BAR"}
      </Typography>

      {/* The actual bus bar — thick wire */}
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: 0,
          right: 0,
          height: hov ? 10 : 8,
          backgroundColor: hov ? "#1a1a2e" : "#37474f",
          borderRadius: 2,
          transform: "translateY(-50%)",
          boxShadow: hov
            ? "0 0 12px rgba(55,71,79,0.6)"
            : "0 2px 6px rgba(0,0,0,0.25)",
          transition: "all 0.15s",
          // Hash marks to indicate it's a bus not a wire
          backgroundImage: hov
            ? "repeating-linear-gradient(90deg, transparent, transparent 28px, rgba(255,255,255,0.15) 28px, rgba(255,255,255,0.15) 30px)"
            : "repeating-linear-gradient(90deg, transparent, transparent 28px, rgba(255,255,255,0.1) 28px, rgba(255,255,255,0.1) 30px)",
        }}
      />

      {/* Handles — spread across the full width for easy connection */}
      <Handle
        id="left"
        type="target"
        position={Position.Left}
        style={{
          background: "#37474f",
          border: "2px solid #fff",
          width: 10,
          height: 10,
          top: "50%",
        }}
      />
      <Handle
        id="right"
        type="source"
        position={Position.Right}
        style={{
          background: "#37474f",
          border: "2px solid #fff",
          width: 10,
          height: 10,
          top: "50%",
        }}
      />

      {/* Multiple top handles spread across width */}
      {[15, 35, 55, 75, 85].map((pct, i) => (
        <Handle
          key={`t${i}`}
          id={`top-${i}`}
          type="target"
          position={Position.Top}
          style={{
            background: "#37474f",
            border: "2px solid #fff",
            width: 8,
            height: 8,
            left: `${pct}%`,
          }}
        />
      ))}

      {/* Multiple bottom handles spread across width */}
      {[15, 35, 55, 75, 85].map((pct, i) => (
        <Handle
          key={`b${i}`}
          id={`bot-${i}`}
          type="source"
          position={Position.Bottom}
          style={{
            background: "#37474f",
            border: "2px solid #fff",
            width: 8,
            height: 8,
            left: `${pct}%`,
          }}
        />
      ))}
    </Box>
  );
}

// ── Stable nodeTypes — MUST be defined outside component ─────
const nodeTypes = { componentNode: ComponentNode, busBarNode: BusBarNode };

// ══════════════════════════════════════════════════════════════
// SCHEMATIC BUILDER
// ══════════════════════════════════════════════════════════════

function SchematicBuilder({
  projectId = null,
  initialSchematic = null,
  initialProject = null,
}) {
  const navigate = useNavigate();
  const nodeIdRef = useRef(1);

  const [mode, setMode] = useState(initialSchematic ? "manual" : null);
  const [project, setProject] = useState({
    ...INITIAL_PROJECT,
    ...initialProject,
  });
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeNodeId, setActiveNodeId] = useState(null);
  const [activeSpecs, setActiveSpecs] = useState({});
  const [activeLabel, setActiveLabel] = useState("");
  const [activeType, setActiveType] = useState("");

  // Operation state
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [error, setError] = useState("");
  const [aiDone, setAiDone] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // ── specsMap — always-fresh node data for dialog reads ────
  const specsMapRef = useRef(new Map());

  // ── Delete node ───────────────────────────────────────────
  const deleteNode = useCallback(
    (id) => {
      setNodes((nds) => nds.filter((n) => n.id !== id));
      setEdges((eds) => eds.filter((e) => e.source !== id && e.target !== id));
      specsMapRef.current.delete(id);
      setDialogOpen(false);
    },
    [setNodes, setEdges],
  );

  // ── Open dialog ───────────────────────────────────────────
  const openDialog = useCallback((id) => {
    const fromMap = specsMapRef.current.get(id);
    setActiveNodeId(id);
    setActiveSpecs(fromMap ? { ...fromMap.specs } : {});
    setActiveLabel(fromMap?.label || "");
    setActiveType(fromMap?.componentType || "");
    setDialogOpen(true);
  }, []);

  // ── Load initial schematic (edit mode) ────────────────────
  useEffect(() => {
    if (!initialSchematic) return;
    const { nodes: sn, edges: se } = initialSchematic;
    if (sn?.length > 0) {
      const restored = sn.map((n) => {
        const compType = n.type || n.data?.componentType || "";
        const isBus = compType === "BUS";
        const specs = n.specs || n.data?.specs || {};
        const label = n.label || n.data?.label || "";

        specsMapRef.current.set(n.id, {
          specs,
          label,
          componentType: isBus ? "BUS" : compType,
        });

        return {
          id: n.id,
          type: isBus ? "busBarNode" : "componentNode",
          position: n.position || {
            x: 100 + Math.random() * 400,
            y: 100 + Math.random() * 300,
          },
          data: {
            id: n.id,
            componentType: isBus ? "BUS" : compType,
            label,
            specs,
            onOpen: openDialog,
            onDelete: deleteNode,
          },
        };
      });
      setNodes(restored);
      nodeIdRef.current = sn.length + 1;
    }
    if (se?.length > 0) {
      setEdges(
        se.map((e) => ({
          ...e,
          animated: true,
          style: { stroke: PRIMARY, strokeWidth: 2.5 },
        })),
      );
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Project field change ──────────────────────────────────
  const handleProjectChange = useCallback((e) => {
    const { name, value } = e.target;
    setProject((p) => ({
      ...p,
      [name]: value,
      ...(name === "voltage_system"
        ? { frequency: value.includes("60Hz") ? "60Hz" : "50Hz" }
        : {}),
    }));
  }, []);

  // ── Connect edges ─────────────────────────────────────────
  const onConnect = useCallback(
    (params) =>
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            animated: true,
            style: { stroke: PRIMARY, strokeWidth: 2.5 },
            label: "✂",
            labelStyle: { fill: "#bbb", fontSize: "10px" },
            labelShowBg: false,
          },
          eds,
        ),
      ),
    [setEdges],
  );

  const onEdgeClick = useCallback(
    (event, edge) => {
      event.stopPropagation();
      setEdges((eds) => eds.filter((e) => e.id !== edge.id));
    },
    [setEdges],
  );

  // ── Add node ──────────────────────────────────────────────
  const addNode = useCallback(
    (compType) => {
      const id = `node_${nodeIdRef.current++}`;
      const isBus = compType === "BUS";
      const comp = COMPONENT_TYPES.find((c) => c.type === compType);
      const label = isBus
        ? "Bus Bar"
        : `${comp?.label} ${nodeIdRef.current - 1}`;

      specsMapRef.current.set(id, {
        specs: {},
        label,
        componentType: compType,
      });

      setNodes((nds) => [
        ...nds,
        {
          id,
          type: isBus ? "busBarNode" : "componentNode",
          position: {
            x: 180 + Math.random() * 320,
            y: 100 + Math.random() * 220,
          },
          data: {
            id,
            componentType: compType,
            label,
            specs: {},
            onOpen: openDialog,
            onDelete: deleteNode,
          },
        },
      ]);
    },
    [openDialog, deleteNode, setNodes],
  );

  // ── Save specs from dialog ────────────────────────────────
  const saveSpecs = useCallback(() => {
    setNodes((nds) =>
      nds.map((n) => {
        if (n.id !== activeNodeId) return n;
        specsMapRef.current.set(n.id, {
          specs: { ...activeSpecs },
          label: activeLabel,
          componentType: n.data.componentType,
        });
        return {
          ...n,
          data: {
            ...n.data,
            specs: { ...activeSpecs },
            label: activeLabel,
            onOpen: openDialog,
            onDelete: deleteNode,
          },
        };
      }),
    );
    setDialogOpen(false);
  }, [
    activeNodeId,
    activeSpecs,
    activeLabel,
    openDialog,
    deleteNode,
    setNodes,
  ]);

  // ── AI Fill ───────────────────────────────────────────────
  const handleAiFill = async () => {
    const nonBus = nodes.filter(
      (n) =>
        n.data.componentType !== "BUS" && n.data.componentType !== "busBarNode",
    );
    if (nonBus.length === 0) {
      setError("Add at least one component first.");
      return;
    }

    setAiLoading(true);
    setError("");
    setAiDone(false);

    try {
      const payload = {
        project: {
          project_number: project.project_number,
          customer_name: project.customer_name,
          vessel_name: project.vessel_name,
          vessel_type: project.vessel_type,
          classification_society: project.classification_society,
          flag_state: project.flag_state,
          voltage_system: project.voltage_system,
          frequency: project.frequency,
          notes: project.notes,
          // space_available_mm removed — handled per component
        },
        components: nonBus.map((n) => ({
          id: n.id,
          type: n.data.componentType,
          label: n.data.label,
          basic_specs: n.data.specs || {},
        })),
        connections: edges.map((e) => ({
          source: e.source,
          target: e.target,
          from_node: e.source,
          to_node: e.target,
        })),
      };

      const response = await api.post("/schematic/ai-fill", payload, {
        timeout: 300000,
      });

      // ── was: const filled = res.data.components  ← res not defined ──
      const filled = response.data.components;

      if (!filled || !Array.isArray(filled)) {
        setError("AI fill returned an unexpected response — please try again.");
        return;
      }

      setNodes((nds) =>
        nds.map((n) => {
          const match = filled.find((f) => f.id === n.id);
          const newSpecs = match?.ai_specs
            ? { ...n.data.specs, ...match.ai_specs }
            : { ...n.data.specs };

          // ── Keep specsMapRef in sync so dialog shows updated specs ──
          specsMapRef.current.set(n.id, {
            specs: newSpecs,
            label: n.data.label,
            componentType: n.data.componentType,
          });

          return {
            ...n,
            data: {
              ...n.data,
              specs: newSpecs,
              onOpen: openDialog,
              onDelete: deleteNode,
            },
          };
        }),
      );

      setAiDone(true);
    } catch (err) {
      if (err.code === "ECONNABORTED" || err.message?.includes("timeout")) {
        setError(
          "AI fill timed out — the schematic has many components. " +
            "Try running AI fill with fewer components or check backend logs.",
        );
      } else {
        setError(
          err?.response?.data?.detail || "AI fill failed — please try again.",
        );
      }
    } finally {
      setAiLoading(false);
    }
  };

  // ── Save schematic ────────────────────────────────────────
  const handleSave = async () => {
    if (!projectId) {
      if (!project.project_number.trim()) {
        setError("Project Number is required.");
        return;
      }
      if (!project.customer_name.trim()) {
        setError("Customer Name is required.");
        return;
      }
      if (!project.vessel_name.trim()) {
        setError("Vessel Name is required.");
        return;
      }
    }
    if (nodes.length === 0) {
      setError("Add at least one component to the schematic.");
      return;
    }

    setLoading(true);
    setError("");
    setSaveSuccess(false);
    try {
      const schematicObj = {
        nodes: nodes.map((n) => ({
          id: n.id,
          type: n.data.componentType,
          label: n.data.label,
          specs: n.data.specs || {},
          position: n.position,
        })),
        edges: edges.map((e) => ({ source: e.source, target: e.target })),
      };

      const components = nodes
        .filter((n) => n.data.componentType !== "BUS")
        .map((n) => ({
          component_type: n.data.componentType,
          tag: n.data.label,
          specs: n.data.specs || {},
        }));

      if (projectId) {
        await api.put(`/schematic/save/${projectId}`, {
          schematic: schematicObj,
          components,
        });
        setSaveSuccess(true);
        // Brief delay so user sees success before navigation
        setTimeout(() => navigate(`/project/${projectId}`), 800);
      } else {
        const payload = {
          ...Object.fromEntries(
            Object.entries(project).filter(([, v]) => v !== "" && v !== null),
          ),
          status: "active",
          schematic: JSON.stringify(schematicObj),
          components,
        };
        const res = await api.post("/projects/", payload);
        navigate(`/project/${res.data.id}`);
      }
    } catch (err) {
      const detail =
        err?.response?.data?.detail || err?.message || "Unknown error";
      if (
        err?.response?.status === 409 ||
        detail.toLowerCase().includes("already exists")
      ) {
        setError(`Project number "${project.project_number}" already exists.`);
      } else {
        setError(`Failed to save: ${detail}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Computed ──────────────────────────────────────────────
  const activeComp = useMemo(
    () => COMPONENT_TYPES.find((c) => c.type === activeType),
    [activeType],
  );

  // AI-filled keys (not in the basic editable fields)
  const aiFilledKeys = useMemo(
    () => Object.keys(activeSpecs).filter((k) => !BASIC_SPEC_KEYS.includes(k)),
    [activeSpecs],
  );

  // ══════════════════════════════════════════════════════════
  // CHOICE SCREEN
  // ══════════════════════════════════════════════════════════
  if (!mode) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: "#f5f7fa",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem 1rem",
        }}
      >
        <Box sx={{ maxWidth: 760, width: "100%" }}>
          <Typography
            variant="h4"
            sx={{ color: DARK, fontWeight: 800, mb: 0.5, textAlign: "center" }}
          >
            New Project
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "#888", mb: 4, textAlign: "center" }}
          >
            Choose how you'd like to set up this project
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 3,
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            {[
              {
                icon: <UploadFileIcon sx={{ fontSize: 44, color: PRIMARY }} />,
                title: "Import from Document",
                desc: "Upload a PDF or Word spec sheet. AI will auto-extract all project details and component specifications.",
                tags: ["PDF", "Word (.docx)", "Auto-Extract", "AI Powered"],
                tagColor: PRIMARY,
                tagBg: "#e8f5e9",
                borderColor: PRIMARY,
                hoverBg: "#f0fafa",
                onClick: () => navigate("/import"),
              },
              {
                icon: <EditNoteIcon sx={{ fontSize: 44, color: "#5c35a0" }} />,
                title: "Build Schematic Manually",
                desc: "Drag and drop components onto the canvas. Enter basic specs and let AI fill the rest from IEC/IEEE standards.",
                tags: [
                  "Drag & Drop",
                  "AI Spec Fill",
                  "SVG Export",
                  "IEEE Standards",
                ],
                tagColor: "#5c35a0",
                tagBg: "#f0eafa",
                borderColor: "#c5b0f0",
                hoverBg: "#f8f4ff",
                onClick: () => setMode("manual"),
              },
            ].map((card) => (
              <Box
                key={card.title}
                onClick={card.onClick}
                sx={{
                  flex: "1 1 300px",
                  maxWidth: 340,
                  borderRadius: "16px",
                  border: `2px solid ${card.borderColor}`,
                  padding: "2rem 1.5rem",
                  textAlign: "center",
                  cursor: "pointer",
                  backgroundColor: "#fff",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: card.hoverBg,
                    transform: "translateY(-4px)",
                    boxShadow: "0 12px 32px rgba(0,0,0,0.1)",
                  },
                }}
              >
                <Box sx={{ mb: 1.5 }}>{card.icon}</Box>
                <Typography
                  variant="h6"
                  sx={{ color: DARK, fontWeight: 700, mb: 1, fontSize: "1rem" }}
                >
                  {card.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#666",
                    mb: 2,
                    lineHeight: 1.6,
                    fontSize: "0.85rem",
                  }}
                >
                  {card.desc}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.5,
                    justifyContent: "center",
                  }}
                >
                  {card.tags.map((t) => (
                    <Chip
                      key={t}
                      label={t}
                      size="small"
                      sx={{
                        backgroundColor: card.tagBg,
                        color: card.tagColor,
                        fontWeight: 600,
                        fontSize: "0.68rem",
                      }}
                    />
                  ))}
                </Box>
              </Box>
            ))}
          </Box>

          <Box sx={{ textAlign: "center", mt: 3 }}>
            <Button
              variant="text"
              onClick={() => navigate("/")}
              sx={{ color: "#999", fontSize: "0.82rem", textTransform: "none" }}
            >
              ← Back to Dashboard
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  // ══════════════════════════════════════════════════════════
  // SCHEMATIC CANVAS
  // ══════════════════════════════════════════════════════════
  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#eef2f7",
      }}
    >
      {/* ── Top bar ──────────────────────────────────────── */}
      <Box
        sx={{
          backgroundColor: DARK,
          color: "#fff",
          padding: "0.5rem 1rem",
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexShrink: 0,
          borderBottom: `3px solid ${PRIMARY}`,
        }}
      >
        <IconButton
          onClick={() =>
            projectId ? navigate(`/project/${projectId}`) : setMode(null)
          }
          sx={{ color: "#fff", p: 0.5, mr: 0.5 }}
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>

        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "0.92rem",
            flexGrow: 1,
            color: "#fff",
            whiteSpace: "nowrap",
          }}
        >
          ⚡ Schematic Builder
          {projectId && (
            <Chip
              label="EDIT MODE"
              size="small"
              sx={{
                ml: 1,
                backgroundColor: "#e65100",
                color: "#fff",
                fontSize: "0.65rem",
                height: 18,
                fontWeight: 700,
              }}
            />
          )}
        </Typography>

        {/* Export SVG — placeholder */}
        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon fontSize="small" />}
          sx={{
            color: "#fff",
            borderColor: "rgba(255,255,255,0.35)",
            borderRadius: "8px",
            fontSize: "0.73rem",
            textTransform: "none",
            whiteSpace: "nowrap",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.08)",
              borderColor: "#fff",
            },
          }}
        >
          Export SVG
        </Button>

        <Button
          variant="outlined"
          size="small"
          startIcon={
            aiLoading ? (
              <CircularProgress size={13} color="inherit" />
            ) : (
              <AutoFixHighIcon fontSize="small" />
            )
          }
          onClick={handleAiFill}
          disabled={aiLoading}
          sx={{
            color: aiLoading ? "#90caf9" : "#fff",
            borderColor: aiLoading
              ? "rgba(144,202,249,0.5)"
              : "rgba(255,255,255,0.35)",
            borderRadius: "8px",
            fontSize: "0.73rem",
            textTransform: "none",
            whiteSpace: "nowrap",
            minWidth: 130, // ← prevents layout shift when text changes
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.08)",
              borderColor: "#fff",
            },
            "&.Mui-disabled": {
              color: "#90caf9",
              borderColor: "rgba(144,202,249,0.5)",
            },
          }}
        >
          {aiLoading ? "Filling specs…" : "AI Fill Specs"}
        </Button>

        <Button
          variant="contained"
          size="small"
          startIcon={
            loading ? (
              <CircularProgress size={13} color="inherit" />
            ) : (
              <SaveIcon fontSize="small" />
            )
          }
          onClick={handleSave}
          disabled={loading || nodes.length === 0}
          sx={{
            backgroundColor: nodes.length === 0 ? "#444" : PRIMARY,
            borderRadius: "8px",
            fontWeight: 700,
            fontSize: "0.73rem",
            textTransform: "none",
            whiteSpace: "nowrap",
            "&:hover": {
              backgroundColor: nodes.length === 0 ? "#444" : "#004749",
            },
            "&.Mui-disabled": {
              backgroundColor: "#444",
              color: "rgba(255,255,255,0.4)",
            },
          }}
        >
          {loading
            ? "Saving…"
            : projectId
              ? "Update Schematic"
              : "Save & Create"}
        </Button>
      </Box>

      {/* ── AI fill progress indicator ──────────────────── */}
      {aiLoading && (
        <Box sx={{ width: "100%", flexShrink: 0 }}>
          <LinearProgress
            sx={{
              height: 3,
              "& .MuiLinearProgress-bar": {
                backgroundColor: "#90caf9",
                backgroundImage:
                  "linear-gradient(90deg, #005E60, #90caf9, #005E60)",
                backgroundSize: "200% 100%",
                animation: "shimmer 1.5s infinite linear",
              },
              "@keyframes shimmer": {
                "0%": { backgroundPosition: "200% 0" },
                "100%": { backgroundPosition: "-200% 0" },
              },
              backgroundColor: "rgba(144,202,249,0.15)",
            }}
          />
        </Box>
      )}

      {/* ── Alert banners ────────────────────────────────── */}
      {error && (
        <Alert
          severity="error"
          onClose={() => setError("")}
          sx={{ mx: 2, mt: 0.5, py: 0.3, borderRadius: "8px" }}
        >
          {error}
        </Alert>
      )}
      {aiDone && (
        <Alert
          severity="success"
          onClose={() => setAiDone(false)}
          sx={{ mx: 2, mt: 0.5, py: 0.3, borderRadius: "8px" }}
        >
          ✅ AI specs filled — click any component to review all fields
        </Alert>
      )}
      {saveSuccess && (
        <Alert
          severity="success"
          onClose={() => setSaveSuccess(false)}
          sx={{ mx: 2, mt: 0.5, py: 0.3, borderRadius: "8px" }}
        >
          ✅ Schematic saved successfully
        </Alert>
      )}

      <Box sx={{ display: "flex", flexGrow: 1, overflow: "hidden" }}>
        {/* ── Sidebar ────────────────────────────────────── */}
        <Box
          sx={{
            width: 220,
            flexShrink: 0,
            backgroundColor: "#fff",
            borderRight: "1px solid #e0e7ef",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
        >
          {!projectId && (
            <>
              <Box sx={{ px: 1.5, pt: 1.2, pb: 0.8, backgroundColor: PRIMARY }}>
                <Typography
                  sx={{
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "0.68rem",
                    letterSpacing: 1.2,
                    textTransform: "uppercase",
                  }}
                >
                  Project Info
                </Typography>
              </Box>
              <Box sx={{ px: 1.2, pt: 1, pb: 1.2 }}>
                {[
                  {
                    name: "project_number",
                    label: "Project No. *",
                    placeholder: "e.g. USN-001",
                  },
                  {
                    name: "customer_name",
                    label: "Customer *",
                    placeholder: "",
                  },
                  {
                    name: "vessel_name",
                    label: "Vessel Name *",
                    placeholder: "",
                  },
                  { name: "flag_state", label: "Flag State", placeholder: "" },
                ].map((f) => (
                  <TextField
                    key={f.name}
                    fullWidth
                    size="small"
                    label={f.label}
                    name={f.name}
                    value={project[f.name]}
                    onChange={handleProjectChange}
                    placeholder={f.placeholder}
                    sx={{
                      mt: 0.8,
                      "& .MuiInputLabel-root": { fontSize: "0.72rem" },
                      "& .MuiInputBase-input": {
                        fontSize: "0.75rem",
                        py: "6px",
                      },
                      "& .MuiOutlinedInput-root": { borderRadius: "6px" },
                    }}
                  />
                ))}
                {[
                  {
                    name: "vessel_type",
                    label: "Vessel Type",
                    options: VESSEL_TYPES,
                  },
                  {
                    name: "classification_society",
                    label: "Classification",
                    options: CLASSIFICATION_SOCIETIES,
                  },
                  {
                    name: "voltage_system",
                    label: "Voltage System",
                    options: VOLTAGE_SYSTEMS,
                  },
                ].map((f) => (
                  <TextField
                    key={f.name}
                    fullWidth
                    select
                    size="small"
                    label={f.label}
                    name={f.name}
                    value={project[f.name]}
                    onChange={handleProjectChange}
                    sx={{
                      mt: 0.8,
                      "& .MuiInputLabel-root": { fontSize: "0.72rem" },
                      "& .MuiInputBase-input": {
                        fontSize: "0.75rem",
                        py: "6px",
                      },
                      "& .MuiOutlinedInput-root": { borderRadius: "6px" },
                    }}
                  >
                    <MenuItem value="">
                      <em>Select…</em>
                    </MenuItem>
                    {f.options.map((o) => (
                      <MenuItem key={o} value={o} sx={{ fontSize: "0.75rem" }}>
                        {o}
                      </MenuItem>
                    ))}
                  </TextField>
                ))}
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                  label="Notes"
                  name="notes"
                  value={project.notes}
                  onChange={handleProjectChange}
                  sx={{
                    mt: 0.8,
                    "& .MuiInputLabel-root": { fontSize: "0.72rem" },
                    "& .MuiInputBase-input": { fontSize: "0.75rem" },
                    "& .MuiOutlinedInput-root": { borderRadius: "6px" },
                  }}
                />
              </Box>
              <Divider />
            </>
          )}

          <Box sx={{ px: 1.5, pt: 1.2, pb: 0.8, backgroundColor: "#f8fafc" }}>
            <Typography
              sx={{
                color: "#546e7a",
                fontWeight: 700,
                fontSize: "0.68rem",
                letterSpacing: 1.2,
                textTransform: "uppercase",
              }}
            >
              Add Components
            </Typography>
            <Typography
              sx={{
                color: "#90a4ae",
                fontSize: "0.62rem",
                mt: 0.3,
                lineHeight: 1.4,
              }}
            >
              Click to add · Hover to delete · Click ✂ wire to remove
            </Typography>
          </Box>

          <Box sx={{ px: 1, pb: 1.5 }}>
            {[
              ...COMPONENT_TYPES,
              { type: "BUS", label: "Bus Bar (conductor)", color: "#37474f" },
            ].map((comp) => (
              <Tooltip
                key="BUS"
                title="Bus Bar — connects multiple components to a shared conductor. Not a component itself — wires route through it automatically."
                placement="right"
              >
                <Box
                  onClick={() => addNode(comp.type)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "7px 10px",
                    borderRadius: "8px",
                    cursor: "pointer",
                    border: `1px solid ${comp.color}22`,
                    mt: 0.5,
                    backgroundColor: "#fff",
                    transition: "all 0.15s",
                    "&:hover": {
                      backgroundColor: `${comp.color}12`,
                      borderColor: `${comp.color}55`,
                      transform: "translateX(3px)",
                    },
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        color: comp.color,
                        lineHeight: 1.2,
                      }}
                    >
                      {comp.type}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.62rem",
                        color: "#999",
                        lineHeight: 1.2,
                      }}
                    >
                      {comp.label}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: comp.color,
                      flexShrink: 0,
                    }}
                  />
                </Box>
              </Tooltip>
            ))}
          </Box>
        </Box>

        {/* ── Canvas ───────────────────────────────────────── */}
        <Box sx={{ flexGrow: 1, position: "relative" }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onEdgeClick={onEdgeClick}
            nodeTypes={nodeTypes}
            fitView
            deleteKeyCode={null}
            connectionLineStyle={{ stroke: PRIMARY, strokeWidth: 2 }}
          >
            <Background
              variant="dots"
              gap={22}
              size={1.5}
              color="#b0bec5"
              style={{ backgroundColor: "#eef2f7" }}
            />
            <Controls style={{ bottom: 40 }} />
            <MiniMap
              nodeColor={(n) =>
                COMPONENT_TYPES.find((c) => c.type === n.data?.componentType)
                  ?.color || "#546e7a"
              }
              style={{
                backgroundColor: "#e8edf5",
                border: "1px solid #d0d7de",
                borderRadius: "8px",
              }}
            />
          </ReactFlow>

          {nodes.length === 0 && (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%,-50%)",
                textAlign: "center",
                pointerEvents: "none",
              }}
            >
              <Typography sx={{ fontSize: "2.5rem", mb: 1 }}>⚡</Typography>
              <Typography
                sx={{ color: "#90a4ae", fontSize: "0.95rem", fontWeight: 700 }}
              >
                Click a component on the left to start
              </Typography>
              <Typography
                sx={{ color: "#b0bec5", fontSize: "0.75rem", mt: 0.5 }}
              >
                Drag handles to connect · Hover to delete · Click ✂ to remove
                wire
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* ══════════════════════════════════════════════════════
          SPEC DIALOG
      ══════════════════════════════════════════════════════ */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}
      >
        <DialogTitle
          sx={{
            backgroundColor: activeComp?.color || PRIMARY,
            color: "#fff",
            py: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Box sx={{ width: 36, height: 36, flexShrink: 0 }}>
            {activeComp && (
              <IEEESymbol type={activeType} color="#fff" size={36} />
            )}
          </Box>
          <Box>
            <Typography
              sx={{ fontWeight: 700, fontSize: "0.92rem", color: "#fff" }}
            >
              {activeComp?.label} — Specifications
            </Typography>
            <Typography
              sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.75)" }}
            >
              {activeLabel}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Component Tag / Label"
                value={activeLabel}
                onChange={(e) => setActiveLabel(e.target.value)}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
            </Grid>

            {[
              {
                key: "ac_voltage",
                label: "AC Voltage (V)",
                placeholder: "e.g. 440",
              },
              {
                key: "frequency_hz",
                label: "Frequency (Hz)",
                placeholder: "e.g. 60",
              },
              {
                key: "power_rating_kw",
                label: "Power Rating (kW)",
                placeholder: "e.g. 500",
              },
              {
                key: "space_taken_mm",
                label: "Space Taken (mm)",
                placeholder: "e.g. 1200 × 800 × 2200",
              },
              {
                key: "ambient_temp_c",
                label: "Ambient Temp (°C)",
                placeholder: "e.g. 45",
              },
              { key: "quantity", label: "Quantity", placeholder: "e.g. 2" },
            ].map((f) => (
              <Grid item xs={12} sm={6} key={f.key}>
                <TextField
                  fullWidth
                  size="small"
                  label={f.label}
                  placeholder={f.placeholder}
                  value={activeSpecs[f.key] || ""}
                  onChange={(e) =>
                    setActiveSpecs((prev) => ({
                      ...prev,
                      [f.key]: e.target.value,
                    }))
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                    "& .MuiInputLabel-root": { fontSize: "0.78rem" },
                    "& .MuiInputBase-input": { fontSize: "0.8rem" },
                  }}
                />
              </Grid>
            ))}

            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={2}
                label="Notes"
                value={activeSpecs.notes || ""}
                onChange={(e) =>
                  setActiveSpecs((prev) => ({ ...prev, notes: e.target.value }))
                }
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
            </Grid>
          </Grid>

          {/* AI-filled specs */}
          {aiFilledKeys.length > 0 && (
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                backgroundColor: "#f1f8f1",
                borderRadius: "8px",
                border: "1px solid #c8e6c9",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  color: "#2e7d32",
                  fontWeight: 700,
                  mb: 1,
                }}
              >
                ✅ AI-Filled Specs (IEC/IEEE Standards)
              </Typography>
              <Grid container spacing={0.5}>
                {aiFilledKeys.map((k) => (
                  <Grid item xs={12} sm={6} key={k}>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <Typography
                        sx={{
                          fontSize: "0.66rem",
                          color: "#666",
                          minWidth: 130,
                        }}
                      >
                        {k.replace(/_/g, " ")}:
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.66rem",
                          color: "#1a1a2e",
                          fontWeight: 600,
                        }}
                      >
                        {String(activeSpecs[k])}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          <Typography
            variant="caption"
            sx={{
              color: "#bbb",
              display: "block",
              mt: 1.5,
              fontSize: "0.68rem",
            }}
          >
            💡 Enter basic specs above, then use <strong>AI Fill Specs</strong>{" "}
            in the toolbar to auto-complete all remaining fields.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.5, gap: 1 }}>
          <Button
            onClick={() => deleteNode(activeNodeId)}
            startIcon={<DeleteIcon />}
            size="small"
            variant="outlined"
            sx={{
              color: "#d32f2f",
              borderColor: "#d32f2f",
              borderRadius: "8px",
              textTransform: "none",
            }}
          >
            Delete
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            onClick={() => setDialogOpen(false)}
            size="small"
            sx={{ color: "#666", textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={saveSpecs}
            variant="contained"
            size="small"
            sx={{
              backgroundColor: activeComp?.color || PRIMARY,
              borderRadius: "8px",
              fontWeight: 700,
              textTransform: "none",
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SchematicBuilder;
